/**
 * Coach Mic — captures the manager's microphone, downsamples to 8kHz,
 * encodes to mulaw, and emits frames over the provided callback. Used
 * by the whisper / barge modes to inject the manager's voice into a
 * live agent call via the existing Twilio Media Stream WebSocket.
 *
 * Pipeline:
 *   getUserMedia() → AudioContext (typically 44.1/48kHz)
 *                  → AudioWorklet captures Float32 frames
 *                  → resample to 8kHz
 *                  → Float32 → Int16 PCM
 *                  → PCM → mulaw bytes
 *                  → base64 → onFrame(base64Payload)
 *
 * Twilio Media Streams expect mulaw 8kHz, base64-encoded, in JSON
 * `media` events. The backend forwards each onFrame payload through
 * the existing /ws/twilio-stream connection back to Twilio.
 */

// ITU-T G.711 mu-law encode (linear PCM → mu-law byte)
const MULAW_BIAS = 0x84;
const MULAW_CLIP = 32635;

function pcmToMulawByte(sample: number): number {
  let sign = (sample >> 8) & 0x80;
  if (sign) sample = -sample;
  if (sample > MULAW_CLIP) sample = MULAW_CLIP;
  sample += MULAW_BIAS;

  let exponent = 7;
  for (let mask = 0x4000; (sample & mask) === 0 && exponent > 0; mask >>= 1) {
    exponent--;
  }
  const mantissa = (sample >> (exponent + 3)) & 0x0f;
  const mulaw = ~(sign | (exponent << 4) | mantissa) & 0xff;
  return mulaw;
}

function pcmToMulaw(pcm: Int16Array): Uint8Array {
  const out = new Uint8Array(pcm.length);
  for (let i = 0; i < pcm.length; i++) out[i] = pcmToMulawByte(pcm[i]);
  return out;
}

/**
 * Resample Float32 audio (any input rate) → Float32 8kHz using
 * linear interpolation. Audio quality is fine for voice — Twilio's
 * call audio is also 8kHz mulaw, so we'd lose nothing by going
 * higher anyway.
 */
function resampleTo8k(input: Float32Array, inputRate: number): Float32Array {
  if (inputRate === 8000) return input;
  const ratio = inputRate / 8000;
  const outLen = Math.floor(input.length / ratio);
  const out = new Float32Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const srcIdx = i * ratio;
    const i0 = Math.floor(srcIdx);
    const i1 = Math.min(i0 + 1, input.length - 1);
    const frac = srcIdx - i0;
    out[i] = input[i0] * (1 - frac) + input[i1] * frac;
  }
  return out;
}

function float32ToInt16(input: Float32Array): Int16Array {
  const out = new Int16Array(input.length);
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

function uint8ToBase64(bytes: Uint8Array): string {
  // Browser-safe base64 encode without spread (avoids stack overflow on large arrays)
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

export interface CoachMicHandle {
  stop: () => void;
  getVolumeLevel: () => number;  // 0..1, RMS of current frame
}

/**
 * Start capturing the manager's mic and emit base64 mulaw frames.
 * Call the returned `stop()` to release the mic + audio context.
 *
 * @param onFrame - called with each ~20ms frame of mulaw audio (base64)
 * @param onError - called if mic permission denied or capture fails
 */
export async function startCoachMic(
  onFrame: (base64Payload: string) => void,
  onError?: (err: Error) => void,
  /**
   * Specific microphone to capture from (2026-07-25). Supervisors on a headset
   * need to pick it explicitly — without this the browser default wins, which is
   * often the laptop's built-in mic while they're wearing a headset.
   * Omit / undefined = browser default.
   */
  inputDeviceId?: string | null,
): Promise<CoachMicHandle> {
  let stream: MediaStream | null = null;
  let audioContext: AudioContext | null = null;
  let processor: ScriptProcessorNode | null = null;
  let source: MediaStreamAudioSourceNode | null = null;
  let analyser: AnalyserNode | null = null;
  let stopped = false;
  let lastVolume = 0;

  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        // `exact` would hard-fail if the device vanished (headset unplugged
        // mid-session); a plain deviceId lets the browser fall back to default.
        ...(inputDeviceId ? { deviceId: inputDeviceId } : {}),
        echoCancellation: true,
        noiseSuppression: true,
        // autoGainControl OFF — the browser's AGC pumps gain up during
        // silences then suddenly compresses on speech onset, producing
        // an audible "whoosh / breath" sound the agent hears on whisper.
        // Whisper is short bursts; manual gain is steadier and matches
        // the agent's expectation of a steady "coaching voice".
        autoGainControl: false,
        // Browsers will pick a sample rate the device supports — we
        // resample below regardless.
      },
      video: false,
    });

    const Ctx = window.AudioContext || (window as any).webkitAudioContext;
    // latencyHint='interactive' shrinks the platform audio buffer for
    // lower mic→inject latency. Agents reported whisper feeling delayed
    // ("I said something 1s ago and it just arrived") — this halves
    // perceived delay on most desktops.
    audioContext = new Ctx({ latencyHint: 'interactive' });
    source = audioContext.createMediaStreamSource(stream);

    // Volume meter (separate from the encode chain so it works even
    // if the encoder hasn't received frames yet)
    analyser = audioContext.createAnalyser();
    analyser.fftSize = 512;
    source.connect(analyser);

    // ScriptProcessorNode is deprecated but works everywhere. AudioWorklet
    // would be cleaner; revisit if browser support justifies the rewrite.
    // Buffer was 4096 samples (~85-92ms) — too chunky for real-time
    // whisper. Halved to 2048 (~42-46ms) which matches Twilio's own
    // 20ms frame cadence much better and feels closer to an instant
    // coaching voice. CPU cost is negligible on modern hardware
    // (~0.1% extra on M1, ~0.5% on a Chromebook).
    const BUFFER_SIZE = 2048;
    processor = audioContext.createScriptProcessor(BUFFER_SIZE, 1, 1);
    processor.onaudioprocess = (event) => {
      if (stopped) return;
      const input = event.inputBuffer.getChannelData(0);

      // Update volume meter (RMS)
      let sum = 0;
      for (let i = 0; i < input.length; i++) sum += input[i] * input[i];
      lastVolume = Math.min(1, Math.sqrt(sum / input.length) * 4); // *4 for visibility

      const resampled = resampleTo8k(input, audioContext!.sampleRate);
      const pcm = float32ToInt16(resampled);
      const mulaw = pcmToMulaw(pcm);
      onFrame(uint8ToBase64(mulaw));
    };

    source.connect(processor);
    // Required for ScriptProcessor to fire callbacks in some browsers,
    // but we don't actually want to play the manager's voice locally.
    // Connect to a muted gain node instead of destination.
    const muteGain = audioContext.createGain();
    muteGain.gain.value = 0;
    processor.connect(muteGain);
    muteGain.connect(audioContext.destination);
  } catch (err) {
    onError?.(err as Error);
    throw err;
  }

  return {
    stop: () => {
      stopped = true;
      try { processor?.disconnect(); } catch {}
      try { source?.disconnect(); } catch {}
      try { analyser?.disconnect(); } catch {}
      try {
        stream?.getTracks().forEach(t => t.stop());
      } catch {}
      audioContext?.close().catch(() => {});
    },
    getVolumeLevel: () => lastVolume,
  };
}
